<template>
    <IdeoFormGroup class="col-md-12" :label="$t('[[[Wybierz sekcję]]]')" :invalid-feedback="form.$errors.first('settings.sectionId')" :state="form.$errors.state('settings.sectionId')" required>
        <IdeoAutocomplete
            v-model="form.settings.sectionId"
            name="sectionId"
            track-by="id"
            :placeholder="$t('[[[Wyszukaj sekcję...]]]')"
            :default-label="$t('[[[Brak sekcji]]]')"
            :fetch="(id: number) => fetchSection(id)"
            :search="(query: string, limit: number) => searchSection(query, limit)"
        >
            <template #selected="{ option }">
                <div class="text-wrap">
                    {{ option.name }}
                </div>
            </template>
            <template #option="{ option }">
                <div class="text-wrap">
                    {{ option.name }}
                </div>
            </template>
        </IdeoAutocomplete>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Etykiety rodzajowe]]]')" :invalid-feedback="form.$errors.first('settings.labelIds')" :state="form.$errors.state('settings.labelIds')">
        <div v-for="(labelId, i) in labelIds" :key="labelId" class="d-flex mb-1">
            <div class="flex-fill me-2">
                <div class="input-group flex-fill me-2">
                    <div class="input-group-prepend me-2">
                        <span class="input-group-text">{{ i + 1 }}</span>
                    </div>
                    <IdeoFormGroup class="flex-fill me-2">
                        <IdeoFormSelect v-model="form.settings.labelIds[i]" name="labelId" :options="getFilteredLabels(i)" value-field="id" text-field="name" />
                    </IdeoFormGroup>
                    <div class="input-group-append">
                        <ActionButton variant="danger" icon="far fa-times" :title="$t('[[[Usuń etykietę]]]')" :confirm="$t('[[[Potwierdzenie usunięcia]]]')" @click="removeLabel(i)" />
                    </div>
                </div>
            </div>
            <div>
                <ActionButton variant="secondary" icon="far fa-arrow-up" :title="$t('[[[Przenieś wyżej]]]') " :disabled="i == 0" @click="labelUp(i)" />
                <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Przenieś niżej]]]')" :disabled="i == labelIds.length - 1" @click="labelDown(i)" />
            </div>
        </div>
        <div v-if="anyLabelLeft()" class="pt-1">
            <ActionButton variant="light" class="w-100" icon="far fa-plus" :text="$t('[[[Dodaj etykietę]]]')" @click="addLabel()" />
        </div>
    </IdeoFormGroup>
</template>

<script lang="ts" setup>
import { computedAsync } from '@vueuse/core';

import type { ListItemModel as SectionListItemModel } from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';
import SectionsService from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';
import type { ListItemOptionsModel as LabelListItemOptionsModel } from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';
import LabelsService from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';

defineOptions({
    name: 'WkArticlesSectionLabelsSettings'
});

const { $log } = useLogging();
const { $alert } = useAlerts();

interface FormSettings
{
    settings: {
        sectionId: number;
        labelIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const labels = ref<LabelListItemOptionsModel[]>([]);
const defaults: FormSettings['settings'] = {
    sectionId: 0,
    labelIds: []
};

const form = computed(() => model.value);

const labelIds = computedAsync(async () =>
{
    return form.value.settings.labelIds || [];
});

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };

    await loadLabels();
});

const fetchSection = async (id: number): Promise<SectionListItemModel> =>
{
    const response = await SectionsService.fetchAutocomplete(id);

    return response || null;
};

const searchSection = async (query: string, limit: number): Promise<SectionListItemModel[]> =>
{
    return await SectionsService.getAutocomplete(query, limit, [], true);
};

const loadLabels = async (): Promise<void> =>
{
    try
    {
        labels.value = await LabelsService.getOptions();
    }
    catch (ex)
    {
        labels.value = [];

        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
};

const addLabel = async (): Promise<void> =>
{
    form.value.settings.labelIds.push(0);
};

const removeLabel = async (index: number): Promise<void> =>
{
    const labelIds = form.value.settings.labelIds.slice();

    labelIds.splice(index, 1);
    form.value.settings.labelIds = labelIds;
};

const labelUp = async (index: number): Promise<void> =>
{
    if (index > 0)
    {
        const labelIds = form.value.settings.labelIds.splice(index, 1);

        form.value.settings.labelIds.splice(index - 1, 0, labelIds[0]);
    }
};

const labelDown = async (index: number): Promise<void> =>
{
    if (index < form.value.settings.labelIds.length - 1)
    {
        const labelIds = form.value.settings.labelIds.splice(index, 1);

        form.value.settings.labelIds.splice(index + 1, 0, labelIds[0]);
    }
};

const getFilteredLabels = (index: number): LabelListItemOptionsModel[] =>
{
    const otherLabelIds = labelIds.value.filter((labelId, i) => i !== index);

    return labels.value.filter(label => !otherLabelIds.includes(label.id));
};

const anyLabelLeft = (): boolean =>
{
    return labels.value.filter(label => !labelIds.value.includes(label.id)).length > 0;
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            sectionId: schema.number().nullable()
                .required($t('[[[Wybieranie sekcji jest wymagane]]]'))
                .typeError($t('[[[Wybieranie sekcji jest wymagane]]]')),
            labelIds: schema.array(
                schema.number().moreThan(0, $t('[[[Nie wybrano etykiety rodzajowej]]]'))
            )
        })
    }));
}

defineExpose({
    validate
});
</script>
